import { navigateTo } from "@/lib/navigation-service";
import { useModalStore } from "@/store/use-modal-store";
import {
  resolveMonetizationSource,
  type MonetizationSource,
} from "@/lib/monetization-analytics";

const PAYMENT_REQUIRED_STATUS = 402;
const MODAL_COOLDOWN_MS = 2000;

let lastPaymentRequiredModalAt = 0;
let paymentRequestCounter = 0;

const nextPaymentRequestId = (): string => {
  paymentRequestCounter += 1;
  return `payment_${Date.now()}_${paymentRequestCounter}`;
};

/**
 * 402'nin kullanıcıya gösterilebilir sebebi.
 *
 * Backend gövdesindeki üretici kodu ("ai_billing_*") doğrudan arayüze
 * taşınmıyor; burada sabit bir genel koda çevriliyor. Modal metnini bu kod
 * seçiyor.
 */
export type PaymentRequiredCode =
  | "PAYMENT_REQUIRED"
  | "DAILY_LIMIT_REACHED"
  | "ACCOUNT_UNAVAILABLE";

/**
 * Gövdedeki üretici kodunu genel koda çevirir.
 *
 * **Fail-closed:** gövde okunamadığında, beklenmedik bir şekil geldiğinde ya da
 * kod tanınmadığında `ACCOUNT_UNAVAILABLE` dönülür. Bilinmeyeni "bakiyeniz
 * yetersiz" diye sunmak yanlış yönlendirir — kullanıcıyı kredi yüklemeye
 * gönderdiğimiz halde sorun bakiye olmayabilir.
 */
export const resolvePaymentRequiredCode = (
  body: unknown,
): PaymentRequiredCode => {
  const producerCode =
    typeof body === "object" && body !== null && "code" in body
      ? (body as { code?: unknown }).code
      : undefined;

  if (producerCode === "ai_billing_insufficient_balance") {
    return "PAYMENT_REQUIRED";
  }
  if (producerCode === "ai_billing_daily_limit_reached") {
    return "DAILY_LIMIT_REACHED";
  }
  return "ACCOUNT_UNAVAILABLE";
};

interface PaymentRequiredPayload {
  status?: number;
  // 402 dönen isteğin URL'i — analytics'te süreci (generation/onboarding/chat)
  // belirlemek için kullanılır.
  requestUrl?: string;
  // Süreç doğrudan biliniyorsa (ör. streaming chat) URL tahminini atlar.
  source?: MonetizationSource;
  // 402 yanıtının gövdesi — sebebi ayırt etmek için. Okunamadıysa verilmeyebilir;
  // o durumda fail-closed davranılır.
  body?: unknown;
}

export const isPaymentRequiredStatus = (status?: number): boolean =>
  status === PAYMENT_REQUIRED_STATUS;

export const handlePaymentRequiredMiddleware = ({
  status,
  requestUrl,
  source,
  body,
}: PaymentRequiredPayload): boolean => {
  if (!isPaymentRequiredStatus(status)) {
    return false;
  }

  const monetizationSource = source ?? resolveMonetizationSource(requestUrl);
  const paymentCode = resolvePaymentRequiredCode(body);
  const paymentRequestId = nextPaymentRequestId();
  const canPurchaseCredits = paymentCode !== "ACCOUNT_UNAVAILABLE";

  const now = Date.now();
  const modalStore = useModalStore.getState();

  // Aynı anda çok sayıda request 402 dönerse modal spam'ini engelle.
  if (
    modalStore.isOpen &&
    modalStore.type === "paymentRequired" &&
    modalStore.data?.paymentCode === paymentCode &&
    modalStore.data?.monetizationSource === monetizationSource &&
    now - lastPaymentRequiredModalAt < MODAL_COOLDOWN_MS
  ) {
    return true;
  }

  lastPaymentRequiredModalAt = now;

  // Open the modal in fallback (no-coupon) mode immediately so the user always
  // gets feedback, then upgrade it to coupon mode once we know the user is
  // eligible. Dynamic import avoids the api-client <-> middleware import cycle.
  modalStore.openPaymentRequiredModal({
    coupon: null,
    monetizationSource,
    paymentCode,
    paymentRequestId,
    ...(canPurchaseCredits
      ? {
          onConfirm: () => {
            navigateTo("/pricing");
          },
        }
      : {}),
  });

  // Hesap erişilemezken kredi satın alma sorunu çözmez; kupon ve pricing CTA
  // göstermek fail-closed metinle çelişir. Günlük limit ve bakiye akışlarının
  // mevcut satın alma davranışı korunur.
  if (!canPurchaseCredits) return true;

  void (async () => {
    try {
      const { queryService } = await import("@/api/services");
      const coupon = await queryService.getActiveCoupon("token_ended");
      if (!coupon) return;

      // Only enrich if the payment-required modal is still the one on screen.
      const state = useModalStore.getState();
      if (
        state.isOpen &&
        state.type === "paymentRequired" &&
        state.data?.paymentRequestId === paymentRequestId
      ) {
        state.openPaymentRequiredModal({
          coupon,
          monetizationSource,
          paymentCode,
          paymentRequestId,
          onConfirm: () => {
            navigateTo("/pricing");
          },
        });
      }
    } catch {
      // Eligibility lookup failed — keep the fallback modal as-is.
    }
  })();

  return true;
};
